import {announce} from '@github-ui/aria-live'
import {Banner, Button, Dialog, Label} from '@primer/react'
import {useEffect, useRef} from 'react'

import {
  isConnected,
  normalizeConnectionStatus,
  pluginDisplayName,
  type ConnectionStatus,
  type Plugin,
} from '../types/connectors'
import type {ConnectorActionSource} from '../utils/connector-analytics'
import {connectionStatusLabel, connectionStatusPresentation} from './ConnectionStatusIcon'
import styles from './ConnectorDetailDialog.module.css'

export interface ConnectorDetailDialogProps {
  plugin: Plugin
  onClose: () => void
  onConnect: (plugin: Plugin, source: Exclude<ConnectorActionSource, 'row'>) => void
  onDisconnect: (plugin: Plugin) => void
  /** Disables the action and shows a spinner while a request is in flight. */
  busy?: boolean
  /** Reason the last connect or disconnect failed, shown inside the dialog. */
  actionError?: string | null
  /** Optional caveat rendered below the details table. */
  note?: string
}

/**
 * Connection state for a connector the user has started setting up. A connector
 * that was never connected has nothing to report, so the section is dropped.
 */
function AccountConnectionDetails({status}: {status?: ConnectionStatus}) {
  const presentation = connectionStatusPresentation(status)
  if (!presentation) return null

  const {Icon, className} = presentation

  return (
    <>
      <h2 className={styles.heading}>Details</h2>
      <dl className={styles.details}>
        <div className={styles.detail}>
          <dt className={styles.detailTerm}>Account connection</dt>
          <dd className={styles.detailValue}>
            {/* Decorative: the status text sits beside it. */}
            <span className={`${styles.statusIcon} ${className ?? ''}`}>
              <Icon size={16} />
            </span>
            {connectionStatusLabel(status)}
          </dd>
        </div>
      </dl>
    </>
  )
}

/**
 * Connector details with the primary lifecycle action.
 *
 * The same dialog serves both tabs. The action is Connect when the connector
 * is not connected and Disconnect when it is.
 */
export function ConnectorDetailDialog({
  plugin,
  onClose,
  onConnect,
  onDisconnect,
  busy = false,
  actionError = null,
  note,
}: ConnectorDetailDialogProps) {
  const name = pluginDisplayName(plugin)
  const connected = isConnected(plugin)
  const status = plugin.connection?.status
  // Consent was started but never finished, so the connector is half set up.
  const setupIncomplete = normalizeConnectionStatus(status) === 'pending'

  const actionLabel = connected ? 'Disconnect' : 'Connect'

  const showSetupIncomplete = setupIncomplete && !busy

  // Each action unmounts as the other takes over, so carry focus across in both
  // directions. Primer's trap does not restore focus when the focused element is
  // removed, which otherwise leaves the user on `document.body`.
  const actionButtonRef = useRef<HTMLButtonElement>(null)
  const retryButtonRef = useRef<HTMLButtonElement>(null)
  const retryPressedRef = useRef(false)
  const wasSetupIncompleteRef = useRef(showSetupIncomplete)
  useEffect(() => {
    const changed = wasSetupIncompleteRef.current !== showSetupIncomplete
    wasSetupIncompleteRef.current = showSetupIncomplete

    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- each button only mounts once the request starts or settles
    if (showSetupIncomplete) {
      // The request settled with the connector still pending, so the footer
      // action has gone away. Reclaim focus only when it was orphaned, so a user
      // who moved to another control keeps their place.
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- the retry button only mounts once the request settles
      if (changed && document.activeElement === document.body) retryButtonRef.current?.focus()
      return
    }

    if (!retryPressedRef.current) return
    retryPressedRef.current = false
    actionButtonRef.current?.focus()
  }, [showSetupIncomplete])

  // The button stops rendering its loading state once the request settles, so
  // announce the outcome separately. A failed action leaves the status alone,
  // which is why the announcement waits for the status to actually change.
  const wasBusyRef = useRef(busy)
  const wasConnectedRef = useRef(connected)
  useEffect(() => {
    const settled = wasBusyRef.current && !busy
    const changed = wasConnectedRef.current !== connected
    wasBusyRef.current = busy
    wasConnectedRef.current = connected
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- the dialog owns the action, but only the parent knows when the request settles
    if (settled && changed) {
      announce(`${name} ${connected ? 'connected' : 'disconnected'}`)
    }
  }, [busy, connected, name])

  // Primer traps focus here, so a failure rendered on the page behind the
  // dialog is unreachable and never read out.
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- the failure arrives from the parent's request, not from an event this dialog sees
    if (actionError) announce(actionError)
  }, [actionError])

  return (
    <Dialog
      onClose={onClose}
      width="xlarge"
      title={
        <span className={styles.title}>
          <span className={styles.titleText}>{name}</span>
          <Label variant="secondary">Connector</Label>
        </span>
      }
      renderFooter={() =>
        showSetupIncomplete ? null : (
          <Dialog.Footer>
            <Button
              ref={actionButtonRef}
              variant={connected ? 'default' : 'primary'}
              loading={busy}
              loadingAnnouncement={`${actionLabel}ing ${name}`}
              onClick={() => (connected ? onDisconnect(plugin) : onConnect(plugin, 'dialog'))}
            >
              {actionLabel}
            </Button>
          </Dialog.Footer>
        )
      }
    >
      {actionError ? (
        <Banner
          variant="critical"
          title="Connector action failed"
          hideTitle
          description={actionError}
          className={styles.actionError}
        />
      ) : null}

      <h2 className={styles.heading}>About</h2>
      <p className={styles.about}>{plugin.description || 'No description provided for this connector.'}</p>

      <AccountConnectionDetails status={status} />

      {note ? <p className={styles.note}>{note}</p> : null}

      {showSetupIncomplete ? (
        <Banner
          variant="warning"
          title="Setup incomplete"
          hideTitle
          description="Setup wasn't complete. Try again to finish connecting."
          actionsLayout="inline"
          primaryAction={
            <Banner.PrimaryAction
              ref={retryButtonRef}
              onClick={() => {
                retryPressedRef.current = true
                onConnect(plugin, 'dialog_retry')
              }}
            >
              Try again
            </Banner.PrimaryAction>
          }
          className={styles.setupIncomplete}
        />
      ) : null}
    </Dialog>
  )
}
